<template>
    <div>
        <div v-for="group in groupedFiles" :key="group.key" class="gallery-group">
            <div class="gallery-group-header">
                {{ group.label }}
                <span class="gallery-group-count">({{ group.files.length }})</span>
            </div>
            <div class="gallery-list">
                <div v-for="file in group.files" :key="file.id" class="gallery-row">
                    <button v-if="isImage(file.file_name)" type="button" class="gallery-thumb gallery-thumb-image"
                        :title="file.file_name" @click="openLightbox(file)">
                        <img :src="file.url" :alt="file.file_name" />
                    </button>

                    <a v-else :href="file.url" target="_blank" rel="noopener" class="gallery-thumb gallery-thumb-file"
                        :title="file.file_name">
                        <i :class="['bi', fileIcon(file.file_name)]"></i>
                        <span class="gallery-ext">{{ fileExt(file.file_name) || 'file' }}</span>
                    </a>

                    <div class="gallery-row-body">
                        <div class="gallery-row-name" :title="file.file_name">{{ file.file_name }}</div>
                        <div v-if="rowMeta(file).length" class="gallery-row-meta">
                            <span v-for="(part, i) in rowMeta(file)" :key="i">{{ part }}</span>
                        </div>
                    </div>

                    <a :href="file.download_url || file.url" :download="file.file_name" target="_blank" rel="noopener"
                        class="gallery-action gallery-download" aria-label="Download" @click.stop>
                        <i class="bi bi-download"></i>
                    </a>
                    <button v-if="allowDelete" type="button" class="gallery-action gallery-remove" aria-label="Remove"
                        @click.stop="$emit('remove', file)">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
            </div>
        </div>

        <Lightbox v-if="lightboxOpen" :images="imageFiles" :initial-index="lightboxIndex"
            @close="lightboxOpen = false" />
    </div>
</template>

<script>
import { format } from "date-fns";
import { fileExt, isImage, fileCategory, fileIcon, fileTypeLabel, formatSize } from "@/services/fileHelper";
import Lightbox from "@/components/Lightbox.vue";

export default {
    name: "LightboxGallery",
    components: { Lightbox },
    props: {
        files: {
            type: Array,
            required: true,
        },
        allowDelete: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["remove"],
    data() {
        return {
            lightboxOpen: false,
            lightboxIndex: 0,
        };
    },
    computed: {
        imageFiles() {
            return this.files.filter((file) => isImage(file.file_name));
        },
        groupedFiles() {
            const groups = [
                { key: "images", label: "Images", files: [] },
                { key: "scans", label: "Digital scans", files: [] },
                { key: "other", label: "Other", files: [] },
            ];
            this.files.forEach((file) => {
                const group = groups.find((g) => g.key === fileCategory(file.file_name));
                if (group) group.files.push(file);
            });
            return groups.filter((g) => g.files.length);
        },
    },
    methods: {
        openLightbox(file) {
            const idx = this.imageFiles.indexOf(file);
            if (idx < 0) return;
            this.lightboxIndex = idx;
            this.lightboxOpen = true;
        },
        fileExt,
        isImage,
        fileIcon,
        fileTypeLabel,
        formatSize,
        rowMeta(file) {
            const parts = [this.fileTypeLabel(file.file_name)];
            if (file.file_size && file.file_size != 0) parts.push(this.formatSize(file.file_size));
            if (file.created_at) parts.push(format(new Date(file.created_at), "d MMM yyyy"));
            return parts;
        },
    },
};
</script>

<style scoped lang="scss">
.gallery-group {
    margin-bottom: 28px;
}

.gallery-group:last-child {
    margin-bottom: 0;
}

.gallery-group-header {
    font-size: 16px;
    color: #374151;
    margin-bottom: 12px;
}

.gallery-group-count {
    color: #9ca3af;
    font-weight: 400;
}

.gallery-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 8px;
}

.gallery-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px 8px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    min-width: 0;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.gallery-row:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border-color: #d1d5db;
}

.gallery-thumb {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border: 0;
    border-radius: 6px;
    background: #f3f4f6;
    overflow: hidden;
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
}

.gallery-thumb-image img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.gallery-thumb-file {
    flex-direction: column;
    gap: 2px;
    color: #6b7280;
}

.gallery-thumb-file i {
    font-size: 24px;
    line-height: 1;
    margin-top: 6px;
}

.gallery-ext {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #9ca3af;
}

.gallery-row-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.gallery-row-name {
    font-size: 16px;
    color: #111827;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.gallery-row-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 14px;
    color: #9ca3af;
}

.gallery-row-meta span {
    position: relative;
}

.gallery-row-meta span+span::before {
    content: "·";
    position: absolute;
    left: -7px;
    color: #d1d5db;
}

.gallery-action {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9ca3af;
    font-size: 12px;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s ease, color 0.15s ease;
}

.gallery-action:hover {
    background: #f3f4f6;
    color: #374151;
}

.gallery-remove:hover {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
}
</style>
